import type { GrantOutcome } from '../../../lib/share-flow';
import { useTranslation } from '../../../lib/i18n';
import { Button, Input, Typography } from '../ui';
import { useShareLinkCopy } from '../files/use-share-link-copy';
import styles from './GrantNotices.module.css';

// What each landed grant left the owner to notice, one block per person.
//
// The share machine holds ONE attempt's notices, so in a multi-recipient run every grant overwrote the
// one before it — and the two notices that matter most are exactly the ones an overwrite destroys: the
// mistyped address that minted a stranger (the only moment a typo is ever observable, since nothing
// about that share will fail afterwards) and the link that only the one grantee without a Symphony
// account needs. So the run collects an outcome per person and they are all rendered.
//
// The identifier heads each block only when there is more than one, because a single share already has
// the address in the notice itself and repeating it reads as a stutter.

export interface GrantNoticesCopy {
  /** Takes `{email}` — nobody answered to that address, so the share created them. */
  invited: string;
  /** The grant is real and the message did not go. */
  notifyFailed: string;
  /** What the link is for. */
  linkHint: string;
}

export function GrantNotices({ outcomes, copy }: { outcomes: GrantOutcome[]; copy: GrantNoticesCopy }) {
  const { t } = useTranslation();
  // Counted over the WHOLE run, not the blocks that survived the filter: with three recipients and
  // one notice, the reader still needs to know which of the three it is about.
  const named = outcomes.length > 1;
  const worth = outcomes.filter((o) => o.invited || o.notifyFailed || o.shareLink);
  if (worth.length === 0) return null;

  return (
    <div className={styles.notices}>
      {worth.map((outcome) => (
        <div key={outcome.identifier} className={styles.block}>
          {named && (
            <Typography variant="p3" color="default" component="p" className={styles.who}>
              {outcome.identifier}
            </Typography>
          )}

          {/* Deliberately NOT an error style: the share worked and the access is waiting. */}
          {outcome.invited && (
            <Typography variant="p3" color="secondary" component="p">
              {t(copy.invited, { email: outcome.identifier })}
            </Typography>
          )}

          {/* The share landed and the MESSAGE did not. Never an error — the access is real — but the
              owner has to know they are the one who has to tell them now. */}
          {outcome.notifyFailed && (
            <Typography variant="p3" color="secondary" component="p">
              {t(copy.notifyFailed)}
            </Typography>
          )}

          {/* Only for a grantee with no Symphony account. Somebody who already has one is told
              in-product and needs no link, so its absence is not a failure to report. */}
          {outcome.shareLink && <ShareLinkRow link={outcome.shareLink} hintKey={copy.linkHint} />}
        </div>
      ))}
    </div>
  );
}


/**
 * One person's link, with its own Copy state.
 *
 * The state has to live per row: one `useShareLinkCopy` shared across the blocks flipped EVERY
 * visible Copy button to "Copied" when the reader pressed one of them, which reads as having copied
 * all of the links and is how you end up pasting the wrong person's.
 */
function ShareLinkRow({ link, hintKey }: { link: string; hintKey: string }) {
  const { t } = useTranslation();
  const { copied, copyLink } = useShareLinkCopy();
  return (
    <div className={styles.linkRow}>
      <Typography variant="p3" color="secondary" component="p">
        {t(hintKey)}
      </Typography>
      {/* Read-only AND copyable. A link you can only select by hand is not a link you can pass on —
          which is the whole job of this block. */}
      <div className={styles.linkField}>
        <Input readOnly value={link} aria-label={t(hintKey)} onFocus={(e) => e.currentTarget.select()} />
        <Button variant="outlined" size="small" onClick={() => void copyLink(link)}>
          {copied ? t('files.share.copied') : t('common.copy')}
        </Button>
      </div>
    </div>
  );
}
